import React, { useState, useRef, useEffect } from 'react';
import { Message, User } from '../types';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';

interface ChatInterfaceProps {
  currentUser: User;
  messages: Message[];
  onSendMessage: (message: string, replyTo?: any) => void;
  onSendFile: (fileName: string, fileUrl: string, fileSize: number, fileType: string, replyTo?: any) => void;
  onDeleteMessage: (messageId: string) => void;
  onClearAll: () => void;
  onLogout: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  currentUser,
  messages,
  onSendMessage,
  onSendFile,
  onDeleteMessage,
  onClearAll,
  onLogout,
  isDarkMode,
  onToggleTheme
}) => {
  const [replyingTo, setReplyingTo] = useState<any>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = messagesContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setShowScrollButton(!isAtBottom);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to delete all messages? This action cannot be undone.')) {
      onClearAll();
    }
  };

  const handleReply = (message: Message) => {
    setReplyingTo({
      id: message.id,
      username: message.username,
      message: message.message || message.fileName || 'File'
    });
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-telegram-darker dark:to-telegram-dark transition-all duration-300">
      {/* Chat Header */}
      <div className="glass border-b border-white/20 dark:border-gray-700/50 p-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          {/* Left side - Chat info */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-telegram-blue to-blue-600 rounded-full flex items-center justify-center shadow-lg">
              <i className="bi bi-people text-white text-lg"></i>
            </div>
            <div>
              <h2 className="font-semibold text-gray-800 dark:text-white">Global Chat</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <i className="bi bi-circle-fill text-green-500 text-xs me-1"></i>Online
              </p>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-2">
            {/* Theme Toggle */}
            <button 
              onClick={onToggleTheme}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200" 
              title="Toggle theme"
            >
              {isDarkMode ? (
                <i className="bi bi-sun text-yellow-400"></i>
              ) : (
                <i className="bi bi-moon text-gray-600"></i>
              )}
            </button>

            {/* More Options */}
            <div className="relative group">
              <button className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200" title="More options">
                <i className="bi bi-three-dots-vertical text-gray-600 dark:text-gray-300"></i>
              </button>
              <div className="absolute right-0 top-full mt-2 w-48 glass rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <button 
                  onClick={handleClearAll}
                  className="w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg transition-colors"
                >
                  <i className="bi bi-trash me-2"></i>Clear All Messages
                </button>
                <button 
                  onClick={onLogout}
                  className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg transition-colors"
                >
                  <i className="bi bi-box-arrow-right me-2"></i>Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div 
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3"
        >
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={message.username === currentUser.username}
              currentUser={currentUser}
              onReply={handleReply}
              onDelete={onDeleteMessage}
            />
          ))}
        </div>

        {/* Message Input */}
        <MessageInput
          onSendMessage={onSendMessage}
          onSendFile={onSendFile}
          replyingTo={replyingTo}
          onCancelReply={cancelReply}
        />
      </div>

      {/* Scroll to Bottom Button */}
      {showScrollButton && (
        <button 
          onClick={scrollToBottom}
          className="fixed bottom-20 right-6 w-12 h-12 bg-telegram-blue hover:bg-blue-600 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-50"
        >
          <i className="bi bi-arrow-down text-white"></i>
        </button>
      )}
    </div>
  );
};

export default ChatInterface;